/**
 * Avatar Component
 * Displays user avatar with fallback to initials
 */

import { type FC } from 'react';
import { cn } from '@/utils/cn';
import { getAvatarById, getDefaultAvatar } from '@/constants/avatars';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  /** Avatar URL/ID from user profile */
  avatarUrl?: string | null;
  /** User display name for initials fallback */
  displayName?: string;
  /** Size variant */
  size?: AvatarSize;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Avatar - User avatar display component
 *
 * Features:
 * - Displays avatar icon based on user selection
 * - Falls back to user initials if no avatar is selected
 * - Falls back to default icon if no display name
 * - Multiple size variants
 * - Uses theme tokens for all styling
 */
export const Avatar: FC<AvatarProps> = ({
  avatarUrl,
  displayName,
  size = 'md',
  className,
}) => {
  // Size mappings for container and icon
  const sizeStyles = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-24 h-24 text-3xl',
  };

  const iconSizes = {
    xs: 12,
    sm: 16,
    md: 24,
    lg: 32,
    xl: 48,
  };

  // Try to get avatar from the avatar options
  const avatarOption = getAvatarById(avatarUrl);

  // Get default avatar icon for fallback
  const defaultAvatar = getDefaultAvatar();
  const DefaultIcon = defaultAvatar.icon;

  // Generate initials from display name
  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        'bg-bg-tertiary border-2 border-border-default',
        'text-text-primary font-semibold',
        'transition-all duration-fast',
        sizeStyles[size],
        className
      )}
      title={displayName}
    >
      {avatarOption ? (
        // Render selected avatar icon
        <avatarOption.icon
          size={iconSizes[size]}
          strokeWidth={2}
          className={avatarOption.color}
        />
      ) : displayName ? (
        // Fallback to initials
        <span className="text-text-secondary">{getInitials(displayName)}</span>
      ) : (
        // Fallback to default icon
        <DefaultIcon
          size={iconSizes[size]}
          strokeWidth={2}
          className="text-text-muted"
        />
      )}
    </div>
  );
};
